import React, { useEffect, useMemo, useState } from "react";
import {
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { AdminAuthAPI } from "../../api/adminAuth";
import { useAdminAuth } from "../../auth/AdminAuthContext";

type Mode = "login" | "bootstrap";

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login, bootstrap, isAuthenticated } = useAdminAuth();

  const redirectTo = useMemo(() => {
    const value = (location.state as any)?.from;
    return typeof value === "string" && value.startsWith("/") ? value : "/";
  }, [location.state]);

  const [mode, setMode] = useState<Mode>("login");
  const [busy, setBusy] = useState(false);
  const [checkingBootstrap, setCheckingBootstrap] = useState(true);
  const [canBootstrap, setCanBootstrap] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(
    searchParams.get("invited") === "1"
      ? "Your admin account is ready. Sign in with the password you created."
      : null,
  );
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState(searchParams.get("email") || "");
  const [password, setPassword] = useState("");

  useEffect(() => {
    let ignore = false;
    setCheckingBootstrap(true);
    (async () => {
      try {
        const status = await AdminAuthAPI.bootstrapStatus();
        if (ignore) return;
        setCanBootstrap(Boolean(status.canBootstrap));
        if (!status.canBootstrap) {
          setMode("login");
        }
      } catch {
        if (ignore) return;
        setCanBootstrap(false);
        setMode("login");
      } finally {
        if (!ignore) setCheckingBootstrap(false);
      }
    })();
    return () => {
      ignore = true;
    };
  }, []);

  if (isAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      if (mode === "bootstrap") {
        if (!canBootstrap) {
          throw new Error("Initial super admin setup is disabled.");
        }
        await bootstrap(fullName.trim(), email.trim(), password);
      } else {
        await login(email.trim(), password);
      }
      navigate(redirectTo, { replace: true });
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Authentication failed. Please try again.";
      setError(String(msg));
      if (
        mode === "bootstrap" &&
        (!canBootstrap || /already been completed/i.test(String(msg)))
      ) {
        setMode("login");
        setNotice(
          "Bootstrap already done. Please log in with an existing admin account.",
        );
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        background: "linear-gradient(135deg,#f8fafc,#eef2ff)",
        padding: 16,
      }}
    >
      <form
        onSubmit={onSubmit}
        style={{
          width: "100%",
          maxWidth: 460,
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: 16,
          padding: 20,
          boxSizing: "border-box",
          boxShadow: "0 10px 28px rgba(15,23,42,0.08)",
        }}
      >
        <h1 style={{ margin: 0, fontSize: 22 }}>BeyondPendants Admin Login</h1>
        <p style={{ marginTop: 6, color: "#475569", fontSize: 13 }}>
          {mode === "login"
            ? "Sign in to access admin tools."
            : "Create the first super admin account (one-time)."}
        </p>

        {mode === "bootstrap" && (
          <input
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="Full name"
            autoComplete="name"
            style={inputStyle}
          />
        )}

        <input
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email"
          type="email"
          autoComplete="email"
          style={inputStyle}
        />
        <input
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password"
          type="password"
          autoComplete={mode === "login" ? "current-password" : "new-password"}
          style={inputStyle}
        />

        {error && (
          <div
            style={{
              marginTop: 10,
              color: "#b91c1c",
              fontSize: 12,
              background: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: 10,
              padding: "8px 10px",
            }}
          >
            {error}
          </div>
        )}
        {notice && (
          <div
            style={{
              marginTop: 10,
              color: "#166534",
              fontSize: 12,
              background: "#f0fdf4",
              border: "1px solid #bbf7d0",
              borderRadius: 10,
              padding: "8px 10px",
            }}
          >
            {notice}
          </div>
        )}

        <button
          type="submit"
          disabled={busy}
          style={{
            marginTop: 12,
            width: "100%",
            borderRadius: 10,
            border: "none",
            background: "#111827",
            color: "#fff",
            padding: "10px 12px",
            fontWeight: 700,
            cursor: "pointer",
            opacity: busy ? 0.75 : 1,
          }}
        >
          {busy
            ? "Please wait..."
            : mode === "login"
              ? "Sign In"
              : "Create Super Admin"}
        </button>

        <div style={{ marginTop: 12, fontSize: 12, color: "#64748b" }}>
          {mode === "login" && !checkingBootstrap && canBootstrap ? (
            <button
              type="button"
              onClick={() => {
                setMode("bootstrap");
                setError(null);
                setNotice(null);
              }}
              style={textButtonStyle}
            >
              First time setup? Create initial super admin.
            </button>
          ) : mode === "bootstrap" ? (
            <button
              type="button"
              onClick={() => {
                setMode("login");
                setError(null);
                setNotice(null);
              }}
              style={textButtonStyle}
            >
              Back to login
            </button>
          ) : !checkingBootstrap ? (
            <span>Initial super admin setup is already completed.</span>
          ) : (
            <span>Checking admin setup…</span>
          )}
        </div>
      </form>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  marginTop: 10,
  width: "100%",
  border: "1px solid #cbd5e1",
  borderRadius: 10,
  padding: "10px 12px",
  fontSize: 14,
  boxSizing: "border-box",
};

const textButtonStyle: React.CSSProperties = {
  border: "none",
  padding: 0,
  background: "transparent",
  color: "#334155",
  textDecoration: "underline",
  cursor: "pointer",
  fontSize: 12,
};
